import type { MedplumClient } from "@medplum/core";
import type { Observation } from "@medplum/fhirtypes";

import { fetchLaborStageObservations } from "@/encounters/components/EncounterLabor/services/labor";
import { joinReferenceString } from "@/utils/medplum";

const INITIAL_DELAY_MS = 1000;
const RETRY_DELAY_MS = 500;
const MAX_MEMBERSHIP_RETRIES = 3;

/**
 * Refetches labor-stage observations after moving a member, waiting for Medplum/bot propagation
 * so the target stage's `hasMember` includes the moved observation before Redux is updated.
 */
export async function fetchLaborStagesAfterMembershipMove(
  medplum: MedplumClient,
  clientId: string,
  encounterId: string,
  targetStageId: string | undefined,
  memberObservationId: string,
): Promise<Observation[]> {
  await new Promise((resolve) => setTimeout(resolve, INITIAL_DELAY_MS));

  let observations: Observation[] = [];
  const memberReference = joinReferenceString("Observation", memberObservationId);

  for (let attempt = 0; attempt < MAX_MEMBERSHIP_RETRIES; attempt += 1) {
    observations = await fetchLaborStageObservations(medplum, clientId, encounterId);

    const targetStageIncludesMember =
      !targetStageId ||
      observations.some(
        (observation) =>
          observation.id === targetStageId &&
          observation.hasMember?.some((member) => member.reference === memberReference),
      );

    if (targetStageIncludesMember) {
      break;
    }

    if (attempt < MAX_MEMBERSHIP_RETRIES - 1) {
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
    }
  }

  return observations;
}
